import { isEqual, isNil } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import {
  allocatorAnalysisSubject,
  allocatorAsOfDateSetting,
  type AllocatorConfig,
  hasUnsavedPortfolioChangesInAllocator,
  hasUnsavedViewChangesInAllocator,
  isAllocatorOpenState,
  openAllocatorSubject,
  openAllocatorSubjectConfig,
  originalAnalysisSubjectQuery,
} from 'venn-state';
import { GetColor } from 'venn-ui-kit';
import { type AnalysisSubject, withSuspense } from 'venn-utils';
import { ConfirmUnsavedChangesModal } from '../../unsaved-changes/ConfirmUnsavedChangesModal';
import { ConfirmUnsavedViewModal } from '../../unsaved-changes/ConfirmUnsavedViewModal';
import { AllocationPanelContext } from '../AllocationPanelContext';
import AllocationPanelForStudio from './AllocationPanelV2RecoilWrapper';
import { StudioAllocatorWindow } from './components';
import { StudioAllocationPanelShimmerBlock } from './StudioAllocationPanelShimmerBlock';
import { StudioAllocatorSubject } from './StudioAllocatorSubject';
import StudioComparisonContextStore from './StudioComparisonContextStore';
import { useAllocationPanelContext } from './useAllocationPanelData';
import { useUnsavedAllocatorChangesModal } from './useUnsavedAllocatorChangesModal';
import { ResizableAllocationPanelWrapper } from './ResizableAllocationPanelWrapper';

const StudioAllocationPanel = () => {
  const hasPortfolioChanges = useRecoilValue(hasUnsavedPortfolioChangesInAllocator);
  const hasViewChanges = useRecoilValue(hasUnsavedViewChangesInAllocator);

  const setIsAllocatorOpen = useSetRecoilState(isAllocatorOpenState);
  const [openSubject, setOpenAllocatorSubject] = useRecoilState(openAllocatorSubject);
  const [analysisSubjectInAllocator, onSubjectUpdate] = useRecoilState(allocatorAnalysisSubject(openSubject));
  const originalSubjectInAllocator = useRecoilValue(originalAnalysisSubjectQuery(openSubject));
  const [allocatorSubjectConfig, setOpenAllocatorSubjectConfig] = useRecoilState(openAllocatorSubjectConfig);
  const [allocationsAsOfDate, setAllocationsAsOfDate] = useRecoilState(allocatorAsOfDateSetting(openSubject));

  const context = useAllocationPanelContext({
    portfolio: originalSubjectInAllocator?.portfolio,
    allocationsAsOfDate,
    setAllocationsAsOfDate,
  });

  // Two settings that can be made within Allocator Panel, sleeve (strategy/investment) and comparison portfolio selection,
  // We sync them to `openAllocatorSubjectConfig` recoil atom, so the view can pick up and track the changes.
  useEffect(() => {
    if (!openSubject) {
      // Only clear the config (that gets saved to analysis view) if there is no `openSubject`.
      // There can be a case when `analysisSubjectInAllocator` is empty, but `openSubject` is set; that signifies that
      // the config came from the saved view, but we haven't loaded the full analysis subject for it yet.
      setOpenAllocatorSubjectConfig(undefined);
    } else if (analysisSubjectInAllocator) {
      const allocatorConfig: AllocatorConfig | undefined = analysisSubjectInAllocator.portfolio && {
        portfolioId: analysisSubjectInAllocator.portfolio.id,
        selectedStrategyId: analysisSubjectInAllocator.strategyId,
        ...(analysisSubjectInAllocator.secondaryPortfolio && analysisSubjectInAllocator.secondaryPortfolioComparisonType
          ? {
              comparisonConfig: {
                comparisonType: analysisSubjectInAllocator.secondaryPortfolioComparisonType,
                comparisonPortfolioId: analysisSubjectInAllocator.secondaryPortfolio.id,
                comparisonPortfolioVersion:
                  analysisSubjectInAllocator.secondaryPortfolioComparisonType === 'SAVED'
                    ? analysisSubjectInAllocator.secondaryPortfolio.version
                    : undefined,
                comparisonSelectedStrategyId: analysisSubjectInAllocator.secondaryStrategy?.id,
              },
            }
          : {}),
      };
      if (isEqual(allocatorConfig, allocatorSubjectConfig)) {
        // Don't update if there was no change to prevent determining difference between two different objects with the
        // same contents.
        return;
      }
      setOpenAllocatorSubjectConfig(allocatorConfig);
    }
  }, [openSubject, analysisSubjectInAllocator, allocatorSubjectConfig, setOpenAllocatorSubjectConfig]);

  const onClosePanel = useCallback(() => {
    if (isNil(originalSubjectInAllocator) || isNil(analysisSubjectInAllocator)) {
      return;
    }
    onSubjectUpdate(undefined);
    setIsAllocatorOpen(false);
    setOpenAllocatorSubject(undefined);
    setOpenAllocatorSubjectConfig(undefined);
  }, [
    analysisSubjectInAllocator,
    onSubjectUpdate,
    originalSubjectInAllocator,
    setIsAllocatorOpen,
    setOpenAllocatorSubject,
    setOpenAllocatorSubjectConfig,
  ]);

  const {
    unsavedAllocatorChangesModalOpen,
    unsavedAllocatorChangesModalMessage,
    onCloseAllocatorClick,
    onConfirmCloseAndLooseChanges: onConfirmClose,
    closeUnsavedAllocatorChangesModal,
  } = useUnsavedAllocatorChangesModal(onClosePanel);

  const [newPortfolioAnalysisSubject, storeNewPortfolioAnalysisSubject] = useState<AnalysisSubject | undefined>();
  const onReplacePortfolioInAllocator = useCallback(
    (subject: AnalysisSubject) => {
      if (!subject.portfolio) {
        return;
      }
      // Reset current portfolio's allocator state
      onSubjectUpdate(undefined);
      // Change selected subject to newly saved one
      setOpenAllocatorSubject({ portfolioId: subject.portfolio.id });
      // Store analysis subject for newly saved portfolio to be updated once `allocatorSubject` recoil selector reloads
      storeNewPortfolioAnalysisSubject(subject);
    },
    [setOpenAllocatorSubject, onSubjectUpdate, storeNewPortfolioAnalysisSubject],
  );
  useEffect(() => {
    if (newPortfolioAnalysisSubject && openSubject?.portfolioId === newPortfolioAnalysisSubject.portfolio?.id) {
      onSubjectUpdate(newPortfolioAnalysisSubject);
      storeNewPortfolioAnalysisSubject(undefined);
    }
  }, [newPortfolioAnalysisSubject, openSubject, onSubjectUpdate]);

  return (
    <AllocationPanelContext.Provider value={context}>
      <ResizableAllocationPanelWrapper>
        <StudioAllocatorWindow>
          <StudioAllocatorSubject onClose={onCloseAllocatorClick} disabled={false} />
          {!isNil(analysisSubjectInAllocator?.portfolio) && (
            <StudioComparisonContextStore analysisSubject={analysisSubjectInAllocator}>
              <AllocationPanelWrapper data-testid="studio-allocator-wrapper">
                <AllocationPanelForStudio onReplacePortfolioInAllocator={onReplacePortfolioInAllocator} />
              </AllocationPanelWrapper>
            </StudioComparisonContextStore>
          )}
          {unsavedAllocatorChangesModalOpen && !hasPortfolioChanges && hasViewChanges && (
            <ConfirmUnsavedViewModal
              message="Are you sure you want to close the Allocator panel?"
              description={unsavedAllocatorChangesModalMessage}
              onConfirm={onConfirmClose}
              onCancel={closeUnsavedAllocatorChangesModal}
            />
          )}
          {unsavedAllocatorChangesModalOpen && hasPortfolioChanges && (
            <ConfirmUnsavedChangesModal
              onProceed={onConfirmClose}
              onReject={closeUnsavedAllocatorChangesModal}
              ignoreUnsavedViewChanges
            />
          )}
        </StudioAllocatorWindow>
      </ResizableAllocationPanelWrapper>
    </AllocationPanelContext.Provider>
  );
};

export default withSuspense(<StudioAllocationPanelShimmerBlock />, React.memo(StudioAllocationPanel));

const AllocationPanelWrapper = styled.div`
  grid-area: allocator-panel;
  background-color: ${GetColor.White};
  overflow: hidden;
`;
